/**
 * Don't use me with react-hook-form
 * instead use frontend/src/components/form/react-hook-form/input-field.tsx
 */

import clsx from "clsx";
import { Label } from "components/label";
import { StateMessage } from "components/state-message";
import React, {
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useRef,
  useState,
} from "react";

import css from "./index.module.scss";

export type InputFieldProps = {
  background?: CSSProperties["background"];
  className?: string;
  isAutoCorrect?: boolean;
  isClearable?: boolean;
  label?: string;
  placeholder?: string;
  prefix?: ReactNode;
  size?: "small" | "medium" | "large";
  state?: "default" | "success" | "error";
  stateMessage?: string;
  suffix?: ReactNode;
} & Omit<React.ComponentPropsWithoutRef<"input">, "prefix" | "size">;

export const InputField = forwardRef(
  (
    {
      autoCapitalize,
      autoComplete,
      background,
      className,
      isAutoCorrect = true,
      isClearable = false,
      label,
      name,
      onChange,
      placeholder,
      prefix,
      size = "medium",
      state = "default",
      stateMessage,
      suffix,
      value,
      ...props
    }: InputFieldProps,
    refForwarded: ForwardedRef<HTMLInputElement>,
  ) => {
    const [hasFocus, setHasFocus] = useState(false);
    const refInternal = useRef<HTMLInputElement | null>(null);

    const onBlur = (e: FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(e);
      setHasFocus(false);
    };

    const onFocus = (e: FocusEvent<HTMLInputElement>) => {
      props.onFocus?.(e);
      setHasFocus(true);
    };

    const wrapperClassnames = clsx(
      css.wrapper,
      hasFocus && css.withFocus,
      state === "success" && css.success,
      state === "error" && css.error,
      css[size],
      className,
    );

    const clear = () => {
      if (refInternal.current && onChange) {
        onChange({
          target: { value: "" },
          type: "change",
        } as ChangeEvent<HTMLInputElement>);
        refInternal.current.focus();
      }
    };

    return (
      <Label className={wrapperClassnames} text={label}>
        <span className={css.inputWrapper} style={{ background }}>
          {prefix && <div className={css.prefix}>{prefix}</div>}

          <input
            {...props}
            autoCapitalize={autoCapitalize || (isAutoCorrect ? "on" : "off")}
            autoComplete={autoComplete || (isAutoCorrect ? "on" : "off")}
            autoCorrect={isAutoCorrect ? "on" : "off"}
            className={clsx(css.input, css[size])}
            name={name}
            placeholder={placeholder}
            ref={(node) => {
              refInternal.current = node;
              if (typeof refForwarded === "function") {
                refForwarded(node);
              } else if (refForwarded) {
                refForwarded.current = node;
              }
            }}
            spellCheck={isAutoCorrect ? "true" : "false"}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
          />

          {isClearable && value && (
            <button className={css.clear} type="button" onClick={clear} />
          )}
          {suffix && <div className={css.suffix}>{suffix}</div>}
        </span>

        <StateMessage message={stateMessage} />
      </Label>
    );
  },
);
